import { ActiveUser } from '@app/shared/user';
import { Center, Group, Paper, SimpleGrid, Stack, Text, ThemeIcon, Title, UnstyledButton } from '@mantine/core';
import { AppKey, landingApp, useAuthentication, useAvailableApps } from '@scoring/logic';
import { DefaultLoader } from '@scoring/ui';
import { IconCalendarEvent, IconClipboardCheck, IconGavel } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router';

const appCards: Record<AppKey, { label: string; description: string; path: string; icon: ReactNode }> = {
    [AppKey.Manager]: {
        label: 'Manager',
        description: 'Set up events, athletes and judges, and run the scoring.',
        path: '/manager',
        icon: <IconCalendarEvent size={22} />,
    },
    [AppKey.Judge]: {
        label: 'Judge',
        description: 'Score the heats you are assigned to, or step in as a judge on an event you run.',
        path: '/judge',
        icon: <IconGavel size={22} />,
    },
    [AppKey.HeadJudge]: {
        label: 'Head Judge',
        description: 'Review the scores on the heats you head judge.',
        path: '/head-judge',
        icon: <IconClipboardCheck size={22} />,
    },
};

export const LandingPage = () => {
    const { user } = useAuthentication();
    const { apps, hasLiveHeadJudging, isLoading } = useAvailableApps();
    // Switch App sends them here to choose, so don't send them straight back
    const picking = (useLocation().state as { pick?: boolean } | null)?.pick === true;

    if (isLoading)
        return (
            <DefaultLoader>
                <Text>Loading Scoring Infrastructure</Text>
            </DefaultLoader>
        );
    const app = picking ? null : landingApp(apps, { hasLiveHeadJudging });
    if (app) return <Navigate to={appCards[app].path} replace />;

    return (
        <Center mih="100vh" p="md">
            <Stack gap={20} w="100%" maw={560}>
                <Group justify="space-between">
                    <Title order={2}>RAWMOTION SCORING</Title>
                    <ActiveUser />
                </Group>
                <Text c="dimmed">Hi {user?.firstName}, where would you like to go?</Text>
                <SimpleGrid cols={{ base: 1, xs: apps.length }}>
                    {apps.map((app) => {
                        const card = appCards[app];
                        return (
                            <UnstyledButton key={app} component={Link} to={card.path}>
                                <Paper withBorder p="md" radius="md" h="100%">
                                    <Stack gap="xs">
                                        <ThemeIcon variant="light" size="lg">
                                            {card.icon}
                                        </ThemeIcon>
                                        <Title order={4}>{card.label}</Title>
                                        <Text size="sm" c="dimmed">
                                            {card.description}
                                        </Text>
                                    </Stack>
                                </Paper>
                            </UnstyledButton>
                        );
                    })}
                </SimpleGrid>
            </Stack>
        </Center>
    );
};
